import * as R from 'ramda';
import React, { useState } from 'react';
import styled from 'styled-components';

import Tabs from 'common/components/tabs/Tabs';
import AccountSummaryCardGroup from 'components/accounts/AccountSummaryCardGroup';
import { AsyncTextButton } from 'components/lib/ui/AsyncTextButton';
import Card from 'components/lib/ui/Card';
import FlexContainer from 'components/lib/ui/FlexContainer';
import PillTab from 'components/lib/ui/tabs/PillTab';
import TabGroup from 'components/lib/ui/tabs/TabGroup';

import { getAccountSummaryComposition } from 'lib/accounts/AccountGraphs';
import useTheme from 'lib/hooks/useTheme';

import type { Web_GetAccountsPageQuery } from 'common/generated/graphql';
import type { ElementOf } from 'common/types/utility';

type AccountSummary = ElementOf<Web_GetAccountsPageQuery, 'accountTypeSummaries'>;

const TABS = [
  {
    label: 'Totals',
    value: 'balance' as const,
  },
  {
    label: 'Percent',
    value: 'percentage' as const,
  },
];

const StyledAsyncTextButton = styled(AsyncTextButton)`
  padding: ${({ theme }) => theme.spacing.default};
  margin: 0 auto;
`;

const StyledTabGroup = styled(TabGroup)`
  margin: -5px 0;
`;

type Props = {
  onClickAccountType: (accountType: string) => void;
  onClickDownloadCsv?: () => Promise<void>;
  isDownloadingCsv?: boolean;
  accountSummaries: AccountSummary[];
};

const AccountsSummaryCard = ({
  onClickAccountType,
  onClickDownloadCsv,
  isDownloadingCsv,
  accountSummaries,
}: Props) => {
  const theme = useTheme();

  const [assets, liabilities] = R.partition<AccountSummary>(R.pathEq(['type', 'group'], 'asset'))(
    accountSummaries,
  );

  const assetComposition = getAccountSummaryComposition(assets, theme);
  const liabilityComposition = getAccountSummaryComposition(liabilities, theme);
  const assetsTotal = R.sum(R.map(R.prop('amount'), assetComposition));
  const liabilitiesTotal = R.sum(R.map(R.prop('amount'), liabilityComposition));

  const [tabIndex, setTabIndex] = useState(0);
  const displayType = TABS[tabIndex].value;

  return (
    <Card
      title="Summary"
      controls={
        <Tabs onChangeIndex={setTabIndex} index={tabIndex}>
          <StyledTabGroup>
            {TABS.map(({ label }, index) => (
              <PillTab key={index} index={index} $background="card">
                {label}
              </PillTab>
            ))}
          </StyledTabGroup>
        </Tabs>
      }
    >
      <AccountSummaryCardGroup
        title="Assets"
        items={assetComposition}
        total={assetsTotal}
        displayType={displayType}
        onClickType={onClickAccountType}
      />
      <AccountSummaryCardGroup
        title="Liabilities"
        items={liabilityComposition}
        total={liabilitiesTotal}
        displayType={displayType}
        onClickType={onClickAccountType}
      />
      <FlexContainer>
        <StyledAsyncTextButton isLoading={isDownloadingCsv} onClick={onClickDownloadCsv}>
          Download CSV
        </StyledAsyncTextButton>
      </FlexContainer>
    </Card>
  );
};

export default AccountsSummaryCard;
